import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Calendar } from "lucide-react";
import { Navbar } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { Seo } from "@/components/Seo";
import { BlogContent } from "@/components/blog/BlogContent";
import { BlogCard } from "@/components/blog/BlogCard";
import { LotusIcon } from "@/components/ui/lotus-icon";
import { getBlogPost, getRelatedPosts } from "@/data/blogPosts";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("nl-NL", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPost(slug) : undefined;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const related = getRelatedPosts(post.slug, 3);
  const url = `https://lunssence.com/blog/${post.slug}`;
  const ctaHref = post.ctaProductSlug
    ? `/producten/${post.ctaProductSlug}`
    : "/producten";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    image: [post.image],
    author: { "@type": "Organization", name: "Lunssence" },
    publisher: {
      "@type": "Organization",
      name: "Lunssence",
      logo: {
        "@type": "ImageObject",
        url: "https://lunssence.com/favicon.svg",
      },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={`${post.metaTitle}`}
        description={post.description}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.image}
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero */}
      <article className="pt-28 md:pt-32 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gold hover:text-gold-light transition-colors mb-8 font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Terug naar blog
          </Link>

          <div className="flex flex-wrap items-center gap-3 mb-6 text-sm">
            <span className="uppercase tracking-widest text-gold font-semibold">
              {post.category}
            </span>
            <span className="text-primary/40">•</span>
            <span className="flex items-center gap-1 text-primary/60">
              <Calendar className="w-4 h-4" /> {formatDate(post.publishedAt)}
            </span>
            <span className="text-primary/40">•</span>
            <span className="flex items-center gap-1 text-primary/60">
              <Clock className="w-4 h-4" /> {post.readTime} leestijd
            </span>
          </div>

          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight mb-8">
            {post.title}
          </h1>

          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-12 border border-gold/20">
            <img
              src={post.image}
              alt={post.imageAlt}
              className="w-full h-full object-cover"
              width={1200}
              height={675}
            />
          </div>

          <BlogContent blocks={post.content} />

          {/* CTA */}
          <div className="mt-16 relative overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-primary to-primary-glow p-8 md:p-12 text-center">
            <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
              <LotusIcon size={300} className="text-gold-light" />
            </div>
            <div className="relative z-10">
              <p className="uppercase tracking-[0.25em] text-gold text-xs font-semibold mb-3">
                Van kennis naar ritueel
              </p>
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-gold-light mb-4">
                {post.ctaLabel}
              </h2>
              <p className="text-gold-light/80 max-w-xl mx-auto mb-6">
                Ontdek onze premium blauwe lotus blends en begin vandaag nog met je
                eigen bewuste ritueel.
              </p>
              <Link
                to={ctaHref}
                className="inline-flex items-center gap-2 bg-gold text-primary px-8 py-3 rounded-full font-semibold hover:bg-gold-light transition-colors"
              >
                {post.ctaLabel} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="pb-24">
          <div className="container mx-auto px-4">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary text-center mb-12">
              Meer lezen
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((p) => (
                <BlogCard key={p.slug} post={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPostPage;
